import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Mail, Phone, FileText, Trash2, Globe, MapPin, Calendar, Plus } from "lucide-react";
import Card from "../components/Card";
import Button from "../components/Button";
import LoadingSkeleton from "../components/LoadingSkeleton";
import CallCard from "../components/CallCard";
import type { Client, Call } from "../types";

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [relatedCalls, setRelatedCalls] = useState<Call[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadClient();
    }
  }, [id]);

  const loadClient = async () => {
    try {
      const response = await fetch(`/api/clients/${id}`);
      if (response.ok) {
        const clientData = await response.json();
        setClient(clientData);

        const callsResponse = await fetch("/api/calls");
        if (callsResponse.ok) {
          const allCalls = await callsResponse.json();
          const clientCalls = allCalls.filter(
            (call: Call) =>
              call.clientId === clientData.id ||
              call.clientName.toLowerCase().includes(clientData.name.toLowerCase())
          );
          setRelatedCalls(clientCalls);
        }
      }
    } catch (error) {
      console.error("Error loading client:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!client || !confirm("Are you sure you want to delete this client?")) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/clients/${client.id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        navigate("/clients");
      }
    } catch (error) {
      console.error("Error deleting client:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <LoadingSkeleton count={3} />;
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Client not found</p>
        <Button variant="ghost" onClick={() => navigate("/clients")} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  const initials = client.name
    .split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div>
      <button
        onClick={() => navigate("/clients")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Clients
      </button>

      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
            <span className="text-teal-700 font-semibold text-2xl">{initials}</span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
              {client.source === "spark_os" && (
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                  Spark OS
                </span>
              )}
            </div>
            {client.company && <p className="text-gray-500">{client.company}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate(`/calls/new?clientId=${client.id}&clientName=${encodeURIComponent(client.name)}`)}
            icon={<Calendar className="w-4 h-4" />}
          >
            Schedule Call
          </Button>
          <Button variant="outline" onClick={handleDelete} loading={isDeleting}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">Contact Information</h3>
          <div className="space-y-3 text-sm">
            {client.company && (
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 className="w-4 h-4 text-gray-400" />
                {client.company}
              </div>
            )}
            {client.email && (
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4 text-gray-400" />
                <a href={`mailto:${client.email}`} className="text-teal-600 hover:underline">
                  {client.email}
                </a>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-gray-400" />
                <a href={`tel:${client.phone}`} className="text-teal-600 hover:underline">
                  {client.phone}
                </a>
              </div>
            )}
            {client.website && (
              <div className="flex items-center gap-2 text-gray-600">
                <Globe className="w-4 h-4 text-gray-400" />
                <a
                  href={client.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:underline"
                >
                  {client.website}
                </a>
              </div>
            )}
            {client.address && (
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4 text-gray-400" />
                {client.address}
              </div>
            )}
            {client.industry && (
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 className="w-4 h-4 text-gray-400" />
                Industry: {client.industry}
              </div>
            )}
            {!client.email && !client.phone && !client.company && (
              <p className="text-gray-500">No contact information added</p>
            )}
          </div>
        </Card>

        {client.notes && (
          <Card>
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Notes
            </h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{client.notes}</p>
          </Card>
        )}
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Related Calls ({relatedCalls.length})
        </h3>
        {relatedCalls.length === 0 ? (
          <Card>
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 mb-3">
                No calls with this client yet
              </p>
              <Button
                size="sm"
                onClick={() => navigate(`/calls/new?clientId=${client.id}&clientName=${encodeURIComponent(client.name)}`)}
                icon={<Plus className="w-4 h-4" />}
              >
                Schedule First Call
              </Button>
            </div>
          </Card>
        ) : (
          relatedCalls.map((call) => (
            <CallCard
              key={call.id}
              call={call}
              onClick={() => navigate(`/calls/${call.id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}
